import React, { useEffect, useMemo, useState } from "react";
import IdentityVerificationModal from "@/components/IdentityVerificationModal";
import IdentityVerificationStatusDisplay from "@/components/IdentityVerificationStatusDisplay";
import { useVerification } from "@hooks/use_verification";
import { Body, Link } from "@sprout/typography";

import { useQuery } from "@tanstack/react-query";
import useTranslation from "@hooks/use_translation";
import axios from "axios";
import { SecondaryButton } from "@sprout/buttons";
import IdentityVerificationLogo from "@/components/ui/identity_verification_logo";
import { identityVerificationPartnerConfig } from "@/utils/identity_verification_provider";

type VerificationStatus = {
  id?: number;
  verification_id?: string;
  status: string;
  partner?: string;
  token?: string;
  completed_at?: string | null;
  failed_at?: string | null;
  created_at?: string;
  updated_at?: string;
  company_name?: string;
};

type VerificationWithTask = VerificationStatus & {
  identity_verification_task_id?: number | null;
  verification_url?: string;
};

const LearnMoreLink = ({ partner }: { partner?: string | null }) => {
  const { t } = useTranslation("identity_verification");
  const partnerConfig = identityVerificationPartnerConfig(partner);

  if (!partnerConfig.learnMoreHref) return null;

  return (
    <Link href={partnerConfig.learnMoreHref} external secondary>
      {t("cta.learn_more")}
    </Link>
  );
};

const IdentityVerificationProfileCard: React.FC = () => {
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const { t } = useTranslation("identity_verification");
  type VerificationShowResponse = {
    verification: VerificationWithTask | null;
    has_pending_identity_verification_task: boolean;
    pending_identity_verification_task_id?: number | null;
    pending_task_company_name?: string | null;
    pending_task_partner?: string | null;
  };

  const { data, isLoading } = useQuery<VerificationShowResponse>({
    queryKey: ["clear-verification-status"],
    queryFn: async () => {
      const response = await axios.get("/identity_verifications/verification");
      return response.data as VerificationShowResponse;
    },
    staleTime: 0,
  });

  const pendingTaskId = data?.pending_identity_verification_task_id;
  const hasPendingTask = !!data?.has_pending_identity_verification_task;

  const { verifyIdentity } = useVerification({
    taskId: pendingTaskId || undefined,
    onSuccess: () => setShowVerifyModal(false),
  });

  const verificationStatus = useMemo<VerificationStatus | null>(() => {
    return (data?.verification as VerificationStatus) || null;
  }, [data]);
  // pending_task_partner can come from a different task than the displayed
  // verification, so it only drives the pending-task branch and the modal.
  const pendingTaskPartner = data?.pending_task_partner;
  // Once the pending task's own verification is underway, the status branch's
  // Continue link is the honest affordance; the pending branch is first-time copy.
  const pendingTaskInProgress =
    verificationStatus?.status === "awaiting_user_input" &&
    data?.verification?.identity_verification_task_id === pendingTaskId;
  const showPendingTaskPrompt = hasPendingTask && !pendingTaskInProgress;

  // Open state must not outlive the branch holding the trigger, or it reopens for the next task.
  useEffect(() => {
    if (!showPendingTaskPrompt) setShowVerifyModal(false);
  }, [showPendingTaskPrompt]);
  const pendingTaskPartnerConfig =
    !isLoading && showPendingTaskPrompt
      ? identityVerificationPartnerConfig(pendingTaskPartner)
      : null;

  if (isLoading || !(hasPendingTask || verificationStatus)) {
    return null;
  }

  return (
    <>
      {showPendingTaskPrompt ? (
        <aside className="bg-white border rounded-lg p-6 w-full">
          <div className="flex flex-col gap-3">
            <div className="flex items-start">
              <IdentityVerificationLogo partner={pendingTaskPartner} />
            </div>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 rounded-lg text-[14px] leading-5 bg-iris-e50">
                {t("badge.action_required")}
              </span>
            </div>
            <Body className="text-base">
              {t(`intro.blurb1.${pendingTaskPartnerConfig!.translationKey}`, {
                provider_name: pendingTaskPartnerConfig!.displayName,
              })}
            </Body>
            <Body className="text-base">
              {t("intro.blurb2", { company_name: data?.pending_task_company_name })}
            </Body>
            <div className="pt-1 flex gap-3 items-center flex-wrap">
              <SecondaryButton size="medium" onClick={() => setShowVerifyModal(true)}>
                {t("cta.verify_identity")}
              </SecondaryButton>
              <LearnMoreLink partner={pendingTaskPartner} />
            </div>
          </div>
        </aside>
      ) : (
        verificationStatus && (
          <aside className="bg-white border rounded-lg p-6 w-full">
            <div className="flex flex-col gap-3">
              <div className="flex items-start">
                <IdentityVerificationLogo partner={verificationStatus.partner} />
              </div>

              <IdentityVerificationStatusDisplay
                status={verificationStatus.status}
                companyName={verificationStatus.company_name}
                verificationUrl={data?.verification?.verification_url}
                completedAt={verificationStatus.completed_at}
                failedAt={verificationStatus.failed_at}
                updatedAt={verificationStatus.updated_at}
                partner={verificationStatus.partner}
              >
                <LearnMoreLink partner={verificationStatus.partner} />
              </IdentityVerificationStatusDisplay>
            </div>
          </aside>
        )
      )}

      <IdentityVerificationModal
        isOpen={showVerifyModal}
        onClose={() => setShowVerifyModal(false)}
        companyName={data?.pending_task_company_name || ""}
        onVerify={verifyIdentity}
        partner={pendingTaskPartner}
      />
    </>
  );
};

export default IdentityVerificationProfileCard;
